import type { IImportColumn } from '@feathr/blackbox';

/*
 * Matches the names the upload step gives the columns of a file it reads as headerless (#9272),
 * and the names the server reads the same way (anhinga#8619).
 */
function positionalColumnName(index: number): string {
  return `Column ${index + 1}`;
}

/**
 * Whether row 1 of the file was read as a header, from the saved column names. Names that are
 * exactly `Column 1` to `Column N`, in order, mean row 1 was read as data. Undefined when there
 * are no columns to read it from.
 */
export function isFirstRowHeader(columnNames: string[]): boolean | undefined {
  if (!columnNames.length) {
    return undefined;
  }
  return !columnNames.every((name, index) => name === positionalColumnName(index));
}

export interface IMappedColumnCount {
  mapped: number;
  total: number;
}

/**
 * Counts the file's columns that map to a Feathr field, out of every column in the file. A column
 * removed from the mapping or left without a field counts against the total, and a column that
 * two mapping rows point at counts once.
 */
export function countMappedColumns(
  columnNames: string[],
  columnMappers: IImportColumn[],
): IMappedColumnCount {
  const fileColumns = new Set(columnNames);
  const mappedColumns = new Set(
    columnMappers
      .filter((mapper) => !!mapper.feathr_attr && fileColumns.has(mapper.column_name))
      .map((mapper) => mapper.column_name),
  );
  return { mapped: mappedColumns.size, total: fileColumns.size };
}

/** The file's columns that no mapping row points at, in file order. */
export function getRemovedColumns(columnNames: string[], columnMappers: IImportColumn[]): string[] {
  const keptColumns = new Set(columnMappers.map((mapper) => mapper.column_name));
  return columnNames.filter((name) => !keptColumns.has(name));
}

export interface IMappingSummaryRow {
  /** Unique per row: a column can appear on more than one mapping row. */
  id: string;
  column: string;
  /** The mapped field's name, or why the column has none. */
  field: { name: string } | { status: 'unmapped' | 'removed' };
}

/**
 * The review's column mapping: each mapping row as file column → field name (or unmapped), then
 * the file's columns left out of the mapping.
 */
export function getMappingSummaryRows(
  columnNames: string[],
  columnMappers: IImportColumn[],
  fieldNames: Record<string, string>,
): IMappingSummaryRow[] {
  const mapped = columnMappers
    .filter((mapper) => mapper.column_name !== '')
    .map((mapper, index): IMappingSummaryRow => {
      const name = mapper.feathr_attr ? fieldNames[mapper.feathr_attr] : undefined;
      return {
        id: `mapped-${index}`,
        column: String(mapper.column_name),
        field: name ? { name } : { status: 'unmapped' },
      };
    });
  const removed = getRemovedColumns(columnNames, columnMappers).map(
    (columnName): IMappingSummaryRow => ({
      id: `removed-${columnName}`,
      column: columnName,
      field: { status: 'removed' },
    }),
  );
  return [...mapped, ...removed];
}
